class ShippingEstimator {
  constructor() {
    this.elements = this._getElements();
    if (!this.elements.container) return;
    
    this._init();
  }

  _getElements() {
    const container = document.querySelector('.shipping-estimator');
    if (!container) return {};
    
    return {
      container,
      form: document.querySelector('#shipping-estimator-form'),
      resultsContainer: document.querySelector('#shipping-results'),
      optionsContainer: document.querySelector('#shipping-options'),
      estimateButton: document.querySelector('#estimate-button'),
      countrySelect: document.querySelector('#AddressCountryShipping'),
      provinceSelect: document.querySelector('#AddressProvinceShipping'),
      zipInput: document.querySelector('[name="shipping-estimator[zip]"]'),
      shippingHeader: container.querySelector('.shipping-estimator-header')
    };
  }

  _init() {
    if (Shopify?.CountryProvinceSelector) {
      new Shopify.CountryProvinceSelector('AddressCountryShipping', 'AddressProvinceShipping', {
        hideElement: 'AddressProvinceContainerShipping'
      });
    }
    
    this._setupEvents();
    this._checkProvinceDiv();
  }

  _setupEvents() {
    // Form submission
    (this.elements.form || this.elements.estimateButton)?.addEventListener(
      this.elements.form ? 'submit' : 'click', 
      (e) => this._handleSubmit(e)
    );

    // Toggle collapse
    this.elements.shippingHeader?.addEventListener('click', () => {
      this.elements.container.classList.toggle('collapsed');
    });

    // Country change
    this.elements.countrySelect?.addEventListener('change', () => {
      this._hideProvinceDiv();
      [100, 300, 500].forEach(delay => 
        setTimeout(() => this._checkProvinceDiv(), delay)
      );
    });
  }

  _handleSubmit(e) {
    e.preventDefault();
    const { country, province, zip } = this._getFormData();
    
    if (!country || !zip) {
      this._showError('Please select a country and enter a zip/postal code.');
      return;
    }

    this._formatZipCode(country, zip);
    this._calculateShipping(country, province, zip);
  }

  _getFormData() {
    return {
      country: this.elements.countrySelect.value,
      province: this.elements.provinceSelect.value,
      zip: this.elements.zipInput.value.trim()
    };
  }

  _formatZipCode(country, zip) {
    if (country === 'Canada' && zip.length === 6 && !zip.includes(' ')) {
      this.elements.zipInput.value = zip.substring(0, 3) + ' ' + zip.substring(3);
    }
  }

  async _calculateShipping(country, province, zip) {
    this._setLoading(true);
    this._clearResults();

    try {
      await this._prepareShippingRates(country, province, zip);
      await this._getShippingRates(country, province, zip);
    } catch (error) {
      this._showError(error.message);
    } finally {
      this._setLoading(false);
    }
  }

  async _prepareShippingRates(country, province, zip) {
    const response = await fetch(`${Shopify.routes.root}cart/prepare_shipping_rates.json`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        'shipping_address[country]': country,
        'shipping_address[province]': province,
        'shipping_address[zip]': zip,
        'shipping_address[phone]': '555-123-4567'
      })
    });

    if (response.status === 422) {
      throw new Error('Please enter a valid zip/postal code for the selected location.');
    }
    
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
  }

  async _getShippingRates(country, province, zip) {
    await this._fetchShippingRates(country, province, zip, 3);
  }

  async _fetchShippingRates(country, province, zip, maxAttempts = 3) {
    const url = `${Shopify.routes.root}cart/async_shipping_rates.json?shipping_address%5Bcountry%5D=${encodeURIComponent(country)}&shipping_address%5Bprovince%5D=${encodeURIComponent(province)}&shipping_address%5Bzip%5D=${encodeURIComponent(zip)}`;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (attempt > 1) await new Promise(resolve => setTimeout(resolve, 1500));
      
      try {
        const response = await fetch(url);
        
        if (response.status === 200) {
          const data = await response.json();
          
          if (data?.shipping_rates?.length > 0) {
            this._renderRates(data.shipping_rates);
            return;
          } else if (data === null || !data) {
            continue;
          } else {
            this._showError('The product in your cart is not available for delivery to your location.');
            return;
          }
        } else if (response.status === 202) {
          continue;
        } else {
          throw new Error(`Server error: ${response.status}`);
        }
      } catch (error) {
        if (attempt === maxAttempts) {
          this._showError('Unable to calculate shipping rates. Please try again.');
          return;
        }
      }
    }
    
    this._showError('Shipping rates calculation timed out. Please try again.');
  }

  _renderRates(rates) {
    this.elements.optionsContainer.innerHTML = `<ul class="list-unstyled shipping-rates-list">${rates.map(rate => `<li class="shipping-rate-item mb-2"><span class="fw-medium">${rate.name}:</span><span class="fw-bold text-primary ms-1">${rate.price === 0 ? "Free" : this._formatMoney(rate.price)}</span></li>`).join("")}</ul>`;
    this._showResults();
  }

  _showResults() {
    this.elements.resultsContainer.style.display = "block";
    const introText = this.elements.resultsContainer.querySelector('.text-muted');
    if (introText) introText.style.display = 'block';
  }

  _clearResults() {
    this.elements.optionsContainer.innerHTML = "";
    this.elements.resultsContainer.style.display = "none";
  }

  _showError(message) {
    this.elements.optionsContainer.innerHTML = `<p class="text-danger mb-0">${message}</p>`;
    this.elements.resultsContainer.style.display = "block";
    const introText = this.elements.resultsContainer.querySelector('.text-muted');
    if (introText) introText.style.display = 'none';
  }

  _setLoading(isLoading) {
    this.elements.estimateButton.disabled = isLoading;
    this.elements.estimateButton.innerHTML = isLoading ? '<span class="spinner-simple"></span>' : 'ESTIMATE';
  }

  _formatMoney(price) {
    const currency = window.Shopify?.currency?.active || "USD";
    const amount = price > 100 ? price / 100 : price;
    return new Intl.NumberFormat('en-US', { style: "currency", currency }).format(amount);
  }

  _hideProvinceDiv() {
    const provinceDiv = document.getElementById('AddressProvinceContainerShipping')?.closest('.col-md-4');
    if (provinceDiv) provinceDiv.style.display = 'none';
  }

  _checkProvinceDiv() {
    const provinceContainer = document.getElementById('AddressProvinceContainerShipping');
    const provinceDiv = provinceContainer?.closest('.col-md-4');
    const provinceSelect = document.getElementById('AddressProvinceShipping');
    
    if (provinceContainer && provinceDiv && provinceSelect) {
      const hasProvinces = provinceSelect.options.length > 1;
      provinceDiv.style.display = hasProvinces ? 'block' : 'none';
      if (hasProvinces) provinceContainer.style.display = 'block';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => new ShippingEstimator());